import {formatPrice, formatToIndianNumber} from '../../../utils/common'
import {toTitleCase} from '../../../utils/string'

export const MIN_DATE_RANGE = '2020-01-01'

const shortNames: Record<number, string> = {
  96: 'NE', // Non-ENDS
  98: 'CP', // Custom Pricing
  45: 'T1', // Tier 1 Prices
  1: 'WH', // Wholesale Approved
  48: 'T2', // Tier 2 Prices
  91: 'TP', // Tier Pro Pricing
  90: 'VP', // Value Tier Pricing
  28: 'INT', // International
}

function formatNumber(num: any) {
  if (num >= 1e9) {
    return (num / 1e9).toFixed(1) + 'B'
  } else if (num >= 1e6) {
    return (num / 1e6).toFixed(1) + 'M'
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(1) + 'K'
  }
  return num.toString()
}

export const CUSTOMER_GROUP_COLUMNS = [
  {key: 'customer_group_name', label: 'Customer Group', headerStyle: 'min-w-250px', isSorted: true},
  {
    key: 'total_orders',
    label: 'Order',
    headerStyle: 'min-w-100px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'total_cost',
    label: 'Total Cost',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'total_revenue',
    label: 'Revenue',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'contributed_revenue_percentage',
    label: 'Contribution to Sales(%)',
    headerStyle: 'min-w-250px',
    isSorted: true,
  },
  {
    key: 'total_profit',
    label: 'Profit',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'profit_percentage',
    label: 'Profit(%)',
    headerStyle: 'w-100px',
    isSorted: true,
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit(%)',
    headerStyle: 'min-w-225px',
    isSorted: true,
  },
  {
    key: 'chart',
    label: 'Chart',
    headerStyle: 'min-w-70px',
    style: 'justify-content-end',
  },
]

export const PROFITABILITY_COLUMNS = [
  {key: 'name', label: 'Customer Name', headerStyle: 'min-w-200px'},
  {key: 'customer_group_name', label: 'Customer Group', headerStyle: 'min-w-250px'},
  {key: 'rep_name', label: 'Sales Rep', headerStyle: 'min-w-200px'},
  {key: 'total_orders', label: 'Order', headerStyle: 'min-w-50px', isColumnTotal: true},
  {key: 'total_cost', label: 'Total Cost', headerStyle: 'min-w-120px', isColumnTotal: true},
  {key: 'total_revenue', label: 'Revenue', headerStyle: 'min-w-120px', isColumnTotal: true},
  {
    key: 'contributed_revenue_percentage',
    label: 'Contribution to Sales(%)',
    headerStyle: 'min-w-250px',
  },
  {key: 'total_profit', label: 'Profit', headerStyle: 'min-w-120px', isColumnTotal: true},
  {
    key: 'profit_percentage',
    label: 'Profit(%)',
    headerStyle: 'w-100px',
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit(%)',
    headerStyle: 'min-w-200px',
    style: 'justify-content-end',
  },
]

export const PROFITABILITY_COLUMNS_WithShorting = [
  {key: 'name', label: 'Customer Name', isSorted: true, headerStyle: 'min-w-200px'},
  {key: 'customer_group_name', label: 'Customer Group', isSorted: true, headerStyle: 'min-w-250px'},
  {key: 'rep_name', label: 'Sales Rep', isSorted: true, headerStyle: 'min-w-200px'},
  {key: 'total_orders', label: 'Order', isSorted: true, headerStyle: 'min-w-50px'},
  {key: 'total_cost', label: 'Total Cost', isSorted: true, headerStyle: 'min-w-150px'},
  {key: 'total_revenue', label: 'Revenue', isSorted: true, headerStyle: 'min-w-120px'},
  {
    key: 'contributed_revenue_percentage',
    label: 'Contribution to Sales(%)',
    headerStyle: 'min-w-250px',
  },
  {key: 'total_profit', label: 'Profit', isSorted: true, headerStyle: 'min-w-120px'},
  {
    key: 'profit_percentage',
    label: 'Profit(%)',
    headerStyle: 'w-100px',
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit(%)',
    headerStyle: 'min-w-200px',
    style: 'justify-content-end',
  },
]

export function PieCharData({piedata, pielabel}: any) {
  const DistributorData = {
    labels: pielabel || [],
    datasets: [
      {
        label: 'Qty',
        data: piedata || [],
        backgroundColor: ['rgba(28, 100, 242)', 'rgba(230, 0, 50)'],
        borderWidth: 1,
        spacing: 2,
      },
    ],
  }
  return DistributorData
}

interface BarChartArgs {
  label: (string | number)[]
  data: (string | number)[]
}

export function BarChartData({data, label}: BarChartArgs) {
  const slicedData = data.slice(0, 5)
  const slicedLabel = label.slice(0, 5)
  const DistributorData = {
    labels: slicedLabel || [],
    datasets: [
      {
        label: 'Profit(%)',
        backgroundColor: 'rgba(0, 92, 230, 0.2)',
        borderColor: 'rgba(0, 92, 230, 1)',
        borderWidth: 0,
        hoverBackgroundColor: 'rgba(0, 92, 230, 1)',
        hoverBorderColor: 'rgba(33, 87, 141, 1)',
        hoverBorderWidth: 0,
        borderRadius: 5,
        barThickness: 20,
        data: slicedData || [],
      },
    ],
  }
  return DistributorData
}

export function getBarChartOptions(mappedLabels: any) {
  return {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 10,
        callbacks: {
          label: function (context: any) {
            const rawLabel = mappedLabels[context.label] || ''
            const price = formatPrice(`${context.raw}`, false)
            const wrappedLabel = rawLabel.match(/.{1,40}/g) || [rawLabel]

            return [...wrappedLabel, 'Profit : ' + price]
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          stepSize: 10000000,
          callback: function (value: number) {
            return formatNumber(value)
          },
        },
      },
    },
  }
}

export const pieChartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  cutout: '85%',
  tooltip: {
    enabled: false,
  },
}

export function transformToBarChartData(data: any[]) {
  return {
    label: data?.map(
      (item) => shortNames[item?.customer_group_id] || `G${item?.customer_group_id}`
    ),
    data: data?.map((item) => item?.total_profit),
  }
}

export const formatPieChartData = (data: any) => {
  return {
    piedata: [data?.revenue, data?.profit], // Use revenue and profit as values
    pielabel: ['Revenue', 'Profit'], // Labels for the chart
  }
}

export function mapCustomerGroups(data: any) {
  const result: any = {}

  data.forEach((item: any) => {
    // Check if the short key exists in shortNames and map it to the customer group name
    const shortKey = shortNames[item.customer_group_id]
    if (shortKey) {
      result[shortKey] = item.customer_group_name
    }
  })

  return result
}

export const CUSTOMER_REP_REPORT_COLUMNS = [
  {
    key: 'customer_rep',
    label: 'Sales Rep Name',
    isSorted: true,
    headerStyle: 'min-w-200px',
  },
  {
    key: 'rep_type',
    label: 'Sales Rep Type',
    isSorted: true,
    headerStyle: 'min-w-250px',
  },
  {
    key: 'total_orders',
    label: 'Order',
    isSorted: true,
    headerStyle: 'min-w-100px',
    isColumnTotal: true,
  },
  {
    key: 'profitable_orders',
    label: 'Profitable',
    isSorted: true,
    headerStyle: 'min-w-120px',
    isColumnTotal: true,
  },
  {
    key: 'non_profitable_orders',
    label: 'Non Profitable',
    isSorted: true,
    headerStyle: 'min-w-175px',
    isColumnTotal: true,
  },
  {
    key: 'total_cost',
    label: 'Cost',
    isSorted: true,
    headerStyle: 'min-w-150px',
    isColumnTotal: true,
  },
  {
    key: 'total_sales',
    label: 'Total Sale',
    isSorted: true,
    headerStyle: 'min-w-150px',
    isColumnTotal: true,
  },
  {
    key: 'contributed_revenue_percentage',
    label: 'Contribution to Sales(%)',
    headerStyle: 'min-w-250px',
    isSorted: true,
  },
  {
    key: 'total_profit',
    label: 'Profit',
    isSorted: true,
    headerStyle: 'min-w-150px',
    isColumnTotal: true,
  },
  {
    key: 'profit_percentage',
    label: 'Profit (%)',
    headerStyle: 'min-w-120px',
    isSorted: true,
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit (%)',
    headerStyle: 'min-w-225px',
    isSorted: true,
  },
  {
    key: 'chart',
    label: 'Chart',
    isSorted: true,
    headerStyle: 'min-w-100px',
    style: 'justify-content-end',
  },
]

export const SUPPLIER_REPORT_COLUMNS = [
  {
    key: 'supplier_name',
    label: 'Supplier Name',
    isSorted: true,
    headerStyle: 'min-w-250px',
  },
  {
    key: 'total_orders',
    label: 'Order',
    isSorted: true,
    headerStyle: 'min-w-100px',
    isColumnTotal: true,
  },
  {
    key: 'total_cost',
    label: 'Cost',
    isSorted: true,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'total_sales',
    label: 'Total Sale',
    isSorted: true,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'total_profit',
    label: 'Profit',
    isSorted: true,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'profit_percentage',
    label: 'Profit (%)',
    headerStyle: 'min-w-150px',
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit (%)',
    headerStyle: 'min-w-225px',
  },
  {
    key: 'chart',
    label: 'Chart',
    isSorted: true,
    headerStyle: 'w-100px',
    style: 'justify-content-end',
  },
]

export const PRODUCT_WISE_REPORT_COLUMNS = [
  {
    key: 'product_name',
    label: 'Product Name',
    isSorted: true,
    headerStyle: 'min-w-400px',
  },
  {key: 'product_sku', label: 'SKU', isSorted: true, headerStyle: 'min-w-150px'},
  {key: 'classification', label: 'Classification', isSorted: true, headerStyle: 'min-w-200px'},
  {key: 'primary_supplier', label: 'Supplier', isSorted: true, headerStyle: 'min-w-250px'},
  {
    key: 'total_orders',
    label: 'Order',
    isSorted: true,
    headerStyle: 'min-w-100px',
    isColumnTotal: true,
  },
  {key: 'total_cost', label: 'Cost', isSorted: true, headerStyle: 'min-w-150px'},
  {key: 'total_sales', label: 'Total Sales', isSorted: true, headerStyle: 'min-w-150px'},
  {key: 'total_profit', label: 'Profit', isSorted: true, headerStyle: 'min-w-150px'},
  {
    key: 'profit_percentage',
    label: 'Profit (%)',
    headerStyle: 'min-w-120px',
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit (%)',
    headerStyle: 'min-w-200px',
  },
]

export const CLASSIFICATION_REPORT_COLUMNS = [
  {
    key: 'classification',
    label: 'Classification Name',
    isSorted: true,
    headerStyle: 'min-w-250px',
  },
  {
    key: 'total_orders',
    label: 'Order',
    isSorted: true,
    headerStyle: 'min-w-100px',
    isColumnTotal: true,
  },
  {
    key: 'total_cost',
    label: 'Cost',
    isSorted: true,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'total_sales',
    label: 'Total Sale',
    isSorted: true,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'total_profit',
    label: 'Profit',
    isSorted: true,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'profit_percentage',
    label: 'Profit (%)',
    headerStyle: 'min-w-150px',
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit (%)',
    headerStyle: 'min-w-225px',
  },
  {
    key: 'chart',
    label: 'Chart',
    isSorted: true,
    headerStyle: 'W-100px',
    style: 'justify-content-end',
  },
]

export const CUSTOMER_GROUP_REPORT_COLUMNS = [
  {
    key: 'customer_group_name',
    label: 'Customer Group Name',
    headerStyle: 'min-w-300px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'total_orders',
    label: 'Order',
    isSorted: true,
    headerStyle: 'min-w-100px',
    isColumnTotal: true,
  },
  {
    key: 'total_cost',
    label: 'Cost',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'total_revenue',
    label: 'Total Sale',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'contributed_revenue_percentage',
    label: 'Contribution to Sales(%)',
    headerStyle: 'min-w-250px',
    isSorted: true,
  },
  {
    key: 'total_profit',
    label: 'Profit',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'profit_percentage',
    label: 'Profit (%)',
    headerStyle: 'min-w-150px',
    isSorted: true,
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit (%)',
    headerStyle: 'min-w-225px',
    isSorted: true,
  },
  {
    key: 'chart',
    label: 'Chart',
    isSorted: true,
    headerStyle: 'min-w-100px',
    style: 'justify-content-end',
  },
]

export const CUSTOMER_TYPE_REPORT_COLUMNS = [
  {
    key: 'customer_type',
    label: 'Customer Type',
    headerStyle: 'min-w-250px',
    isSorted: true,
  },
  {
    key: 'total_orders',
    label: 'Order',
    isSorted: true,
    headerStyle: 'min-w-100px',
    isColumnTotal: true,
  },
  {
    key: 'total_cost',
    label: 'Cost',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'total_sales',
    label: 'Total Sale',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'total_profit',
    label: 'Profit',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'profit_percentage',
    label: 'Profit (%)',
    headerStyle: 'min-w-150px',
    isSorted: true,
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit (%)',
    headerStyle: 'min-w-225px',
    isSorted: true,
  },
  {
    key: 'chart',
    label: 'Chart',
    isSorted: true,
    headerStyle: 'min-w-100px',
    style: 'justify-content-end',
  },
]

export const PURCHASER_REPORT_COLUMNS = [
  {
    key: 'purchaser_name',
    label: 'Purchaser',
    headerStyle: 'min-w-250px',
    isSorted: true,
  },
  {
    key: 'total_orders',
    label: 'Order',
    isSorted: true,
    headerStyle: 'min-w-100px',
    isColumnTotal: true,
  },
  {
    key: 'total_cost',
    label: 'Cost',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'total_sales',
    label: 'Total Sale',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'total_profit',
    label: 'Profit',
    headerStyle: 'min-w-150px',
    isSorted: true,
    isColumnTotal: true,
  },
  {
    key: 'profit_percentage',
    label: 'Profit (%)',
    headerStyle: 'min-w-150px',
    isSorted: true,
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit (%)',
    headerStyle: 'min-w-225px',
    isSorted: true,
  },
  {
    key: 'chart',
    label: 'Chart',
    isSorted: true,
    headerStyle: 'w-100px',
    style: 'justify-content-end',
  },
]

export const ORDER_REPORT_COLUMNS = [
  {
    key: 'order_date',
    label: 'Order Date',
    headerStyle: 'min-w-150px',
    isSorted: true,
  },
  {
    key: 'order_id',
    label: 'Order ID',
    headerStyle: 'min-w-150px',
    isSorted: true,
  },
  {
    key: 'name',
    label: 'Customer Name',
    headerStyle: 'min-w-200px',
    isSorted: true,
  },
  {
    key: 'customer_group_name',
    label: 'Customer group',
    headerStyle: 'min-w-200px',
    isSorted: true,
  },
  {
    key: 'sales_rep_name',
    label: 'Sales Rep Name',
    headerStyle: 'min-w-200px',
    isSorted: true,
  },
  {
    key: 'subtotal',
    label: 'Subtotal',
    headerStyle: 'min-w-150px',
    isSorted: true,
    columnTotalKey: 'subtotal',
  },
  {
    key: 'coupon_discounts',
    label: 'Coupon Discounts',
    headerStyle: 'min-w-200px',
    isSorted: true,
    columnTotalKey: 'couponDiscounts',
  },
  {
    key: 'tax',
    label: 'Tax',
    headerStyle: 'min-w-150px',
    isSorted: true,
    columnTotalKey: 'tax',
  },
  {
    key: 'shipping',
    label: 'Shipping',
    headerStyle: 'min-w-150px',
    isSorted: true,
    columnTotalKey: 'shipping',
  },
  {
    key: 'total',
    label: 'Total',
    headerStyle: 'min-w-150px',
    isSorted: true,
    columnTotalKey: 'total',
  },
  {
    key: 'total_cost',
    label: 'Total Cost',
    headerStyle: 'min-w-150px',
    isSorted: true,
    columnTotalKey: 'cost',
  },
  // {
  //   key: 'total_sell',
  //   label: 'Total sell',
  //   headerStyle: 'min-w-150px',
  //   isSorted: true,
  //   columnTotalKey: 'total_sell',
  // },
  {
    key: 'total_profit',
    label: 'Profit',
    headerStyle: 'min-w-150px',
    isSorted: true,
    columnTotalKey: 'total_profit',
  },
  {
    key: 'profit_percentage',
    label: 'Profit(%)',
    headerStyle: 'min-w-150px',
    isSorted: true,
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit(%)',
    headerStyle: 'min-w-225px',
    isSorted: false,
    style: 'justify-content-end',
  },
]

export const BRAND_REPORT_COLUMNS = [
  {
    key: 'brand_name',
    label: 'Brand',
    isSorted: true,
    headerStyle: 'min-w-250px',
  },
  {
    key: 'purchaser_name',
    label: 'Purchaser',
    isSorted: true,
    headerStyle: 'min-w-200px',
  },
  {
    key: 'total_orders',
    label: 'Orders',
    isSorted: true,
    headerStyle: 'min-w-200px',
  },
  {
    key: 'total_cost',
    label: 'Cost',
    isSorted: true,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'total_revenue',
    label: 'Total Sale',
    isSorted: true,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'total_profit',
    label: 'Profit',
    isSorted: true,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'profit_percentage',
    label: 'Profit (%)',
    isSorted: false,
    headerStyle: 'min-w-150px',
  },
  {
    key: 'contributed_profit_percentage',
    label: 'Contributed Profit (%)',
    isSorted: false,
    headerStyle: 'min-w-225px',
  },
  {
    key: 'chart',
    label: 'Chart',
    isSorted: true,
    headerStyle: 'w-100px',
    style: 'justify-content-end',
  },
]

export const parseBrandReportRow = (row: any) => ({
  brandId: row.brand_id || '-',
  brandName: row.brand_name || '-',
  purchaserName: row.purchaser_name || '-',
  orders: formatToIndianNumber(row?.total_orders) || '-',
  totalCost: row.total_cost ? formatPrice(row.total_cost, false) : '-',
  totalSale: row.total_revenue ? formatPrice(row.total_revenue, false) : '-',
  totalProfit: row.total_profit ? formatPrice(row.total_profit, false) : '-',
  totalProfitPercentage: row.profit_percentage ? row.profit_percentage + '%' : '0%',
  contributedProfitPercentage: row.contributed_profit_percentage
    ? row.contributed_profit_percentage + '%'
    : '0%',
})

export const productWiseReportDefaultColumns = [
  {product_name: 'PRODUCT NAME', default: true},
  {sku: 'SKU', default: true},
  {cost: 'COST', default: true},
  {total_sales: 'TOTAL SALES'},
  {profit: 'PROFIT'},
  {profit_percentage: 'PROFIT (%)'},
  {contributed_profit_percentage: 'CONTRIBUTED PROFIT (%)'},
  {action: 'ACTION'},
]

export const supplierReportDefaultColumns = [
  {supplier_name: 'SUPPLIER NAME', default: true},
  {cost: 'COST', default: true},
  {total_sales: 'TOTAL SALE'},
  {profit: 'PROFIT'},
  {profit_percentage: 'PROFIT (%)'},
  {contributed_profit_percentage: 'CONTRIBUTED PROFIT (%)'},
  {action: 'ACTION'},
]

export const dataToPurchaserReportsCSV = (data: any[]) => {
  const results: any[] = []
  // '$' + item?.total_discount || "$0"
  data.forEach((item) => {
    results.push({
      PURCHASER: item?.purchaser_name || '',
      ORDER: item?.total_orders || 0,
      COST: '$' + item?.total_cost || '$0',
      'TOTAL SALE': '$' + item?.total_sales || 0,
      PROFIT: '$' + item?.total_profit || 0,
      'PROFIT (%)': item?.profit_percentage != null ? `${item.profit_percentage}%` : '0%',
      'CONTRIBUTED PROFIT (%)':
        item?.contributed_profit_percentage != null
          ? `${item.contributed_profit_percentage}%`
          : '0%',
    })
  })

  return results
}

export const dataToCustomerGroupReportCSV = (data: any[]) => {
  const results: any[] = []

  data.forEach((item) => {
    results.push({
      'CUSTOMER GROUP': item?.customer_group_name || '',
      ORDER: item?.total_orders || 0,
      'TOTAL COST': '$' + item?.total_cost || '$0',
      REVENUE: '$' + item?.total_revenue || '$0',
      'CONTRIBUTION TO SALES(%)':
        item?.contributed_revenue_percentage != null
          ? `${item.contributed_revenue_percentage}%`
          : '0%',
      PROFIT: '$' + item?.total_profit || '$0',
      'PROFIT(%)': item?.profit_percentage != null ? `${item.profit_percentage}%` : '0%',
      'CONTRIBUTED PROFIT(%)':
        item?.contributed_profit_percentage != null
          ? `${item.contributed_profit_percentage}%`
          : '0%',
    })
  })

  return results
}

export const dataToCustomerGroupCSV = (data: any[]) => {
  const results: any[] = []

  data.forEach((item) => {
    results.push({
      'CUSTOMER GROUP NAME': item?.customer_group_name || '',
      ORDER: item?.total_orders || 0,
      COST: item?.total_cost != null ? `$${item.total_cost}` : '$0',
      'TOTAL SALE': item?.total_revenue != null ? `$${item.total_revenue}` : '$0',
      'CONTRIBUTION TO SALES(%)':
        item?.contributed_revenue_percentage != null
          ? `${item.contributed_revenue_percentage}%`
          : '0%',
      PROFIT: item?.total_profit != null ? `$${item.total_profit}` : '$0',
      'PROFIT (%)': item?.profit_percentage != null ? `${item.profit_percentage}%` : '0%',
      'CONTRIBUTED PROFIT (%)':
        item?.contributed_profit_percentage != null
          ? `${item.contributed_profit_percentage}%`
          : '0%',
    })
  })

  return results
}

export const dataToCustomerGroupTypeReportCSV = (data: any[]) => {
  const results: any[] = []

  data.forEach((item) => {
    results.push({
      'CUSTOMER TYPE': item?.customer_type || '',
      ORDER: item?.total_orders || 0,
      'TOTAL COST': '$' + item?.total_cost || '$0',
      'TOTAL SALE': '$' + item?.total_sales || '$0',
      PROFIT: '$' + item?.total_profit || '$0',
      'PROFIT(%)': item?.profit_percentage != null ? `${item.profit_percentage}%` : '0%',
      'CONTRIBUTED PROFIT(%)':
        item?.contributed_profit_percentage != null
          ? `${item.contributed_profit_percentage}%`
          : '0%',
    })
  })

  return results
}

export const dataToSalesRepProfitCSV = (data: any[]) => {
  const results: any[] = []

  data.forEach((item) => {
    results.push({
      'SALES REP NAME': item?.customer_rep || '',
      'SALES REP TYPE': item?.rep_type || '',
      ORDER: item?.total_orders || 0,
      COST: item?.total_cost != null ? `$${item.total_cost}` : '$0',
      'TOTAL SALE': item?.total_sales != null ? `$${item.total_sales}` : '$0',
      'CONTRIBUTION TO SALES(%)':
        item?.contributed_revenue_percentage != null
          ? `${item.contributed_revenue_percentage}%`
          : '0%',
      PROFIT: item?.total_profit != null ? `$${item.total_profit}` : '$0',
      'PROFIT (%)': item?.profit_percentage != null ? `${item.profit_percentage}%` : '0%',
      'CONTRIBUTED PROFIT (%)':
        item?.contributed_profit_percentage != null
          ? `${item.contributed_profit_percentage}%`
          : '0%',
    })
  })

  return results
}

export const getReportTable = (searchParams: any) => {
  const {coupon_code_type, is_profitable, ...restSearchParams} = searchParams
  const reportType = Object.keys(restSearchParams)?.toString()

  switch (reportType) {
    case 'customer_group':
      return toTitleCase('Customer Group')
    case 'sales_rep':
      return toTitleCase(
        `Sales Rep ${
          is_profitable && is_profitable !== null
            ? is_profitable === 'true'
              ? '(Profitable)'
              : '(Non Profitable)'
            : ''
        }`
      )
    case 'customer_id':
      return toTitleCase('Customer')
    case 'customer_type':
      return toTitleCase('Customer Type')
    case 'supplier_name':
      return toTitleCase('Supplier')
    case 'classification':
      return toTitleCase('Classification')
    case 'purchaser_name':
      return toTitleCase('Purchaser By Supplier')
    case 'product_id':
      return toTitleCase('Product')
    case 'rep_type':
      return toTitleCase('Sales Rep Type')
    case 'brand':
      return toTitleCase('Purchaser by Brand')
    case 'coupon_code':
      return toTitleCase('Coupon Code')
    case 'profit_percentage':
      return toTitleCase('Profit Percentage')
    default:
      return ''
  }
}

export const profitPercentageDropdownOptions = [
  {
    label: '20% and more',
    value: '20_plus',
  },
  {
    label: '15% to 20%',
    value: '15_to_20',
  },
  {
    label: '10% to 15%',
    value: '10_to_15',
  },
  {
    label: '5% to 10%',
    value: '5_to_10',
  },
  {
    label: '0% to 5%',
    value: '0_to_5',
  },
  {
    label: '0%',
    value: '0',
  },
  {
    label: '0% to -5%',
    value: '-5_to_0',
  },
  {
    label: '-5% to -10%',
    value: '-10_to_-5',
  },
  {
    label: '-10% to -15%',
    value: '-15_to_-10',
  },
  {
    label: '-15% to -20%',
    value: '-20_to_-15',
  },
  {
    label: '-20% and less',
    value: '-20_plus',
  },
]

export const ENTITY_PERFORMANCE_CHART_VIEW_OPTIONS = (startDate: string, endDate: string) => {
  const startDateObj = new Date(startDate)
  const endDateObj = new Date(endDate)
  const timeDiff = endDateObj.getTime() - startDateObj.getTime()
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))

  if (daysDiff <= 30) {
    // Up to 1 month
    return [
      {label: 'Day', value: 'day'},
      {label: 'Week', value: 'week'},
    ]
  } else if (daysDiff <= 180) {
    // Between 1 and 6 months
    return [
      {label: 'Week', value: 'week'},
      {label: 'Month', value: 'month'},
    ]
  } else {
    // More than 6 months
    return [
      {label: 'Month', value: 'month'},
      {label: 'Year', value: 'year'},
    ]
  }
}

export const PROFIT_TREND_CHART_VIEW_OPTIONS = (startDate: string, endDate: string) => {
  const startDateObj = new Date(startDate)
  const endDateObj = new Date(endDate)
  const timeDiff = endDateObj.getTime() - startDateObj.getTime()
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))

  if (daysDiff <= 30) {
    // Up to 1 month
    return [
      {label: 'Day', value: 'day'},
      {label: 'Week', value: 'week'},
    ]
  } else if (daysDiff <= 180) {
    // Between 1 and 6 months
    return [
      {label: 'Week', value: 'week'},
      {label: 'Month', value: 'month'},
    ]
  } else {
    // More than 6 months
    return [
      {label: 'Month', value: 'month'},
      {label: 'Year', value: 'year'},
    ]
  }
}


export const formatPercentage = (value: number, decimals: number = 2): string => {
  return `${value.toFixed(decimals)}%`
}

export const getProfitStatusColor = (profit: number): string => {
  if (profit >= 25) return 'badge-light-success'
  return 'badge-light-warning'
}

export const chartOptions = {
  responsive: true,
  layout: {
    padding: {
      bottom: 40,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
      mode: 'index' as const,
      intersect: false,
      callbacks: {
        label: function (context: any) {
          let label = context.dataset.label || ''
          if (label) {
            label += ': '
          }
          if (context.parsed.y !== null) {
            label += `${context.parsed.y.toFixed(2)}%`
          }
          return label
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: false,
      grid: {
        display: true,
        color: 'rgba(0, 0, 0, 0.1)',
      },
      ticks: {
        callback: (value: string | number) => (typeof value === 'number' ? `${value}%` : value),
        stepSize: 1,
      },
    },
    x: {
      type: 'category' as const,
      grid: {
        display: false,
      },
      ticks: {
        padding: 15,
      },
    },
  },
  elements: {
    line: {
      tension: 0,
      borderWidth: 3,
    },
    point: {
      radius: 6,
      borderWidth: 2,
      backgroundColor: '#3699ff',
      borderColor: '#3699ff',
    },
  },
}

// Weekly Breakdown Table Columns Configuration
export const weeklyBreakdownColumns = [
  {
    key: 'week',
    label: 'WEEK',
    headerStyle: 'min-w-125px',
    isSorted: false,
  },
  {
    key: 'date',
    label: 'DATE',
    headerStyle: 'min-w-125px',
    isSorted: false,
  },
  {
    key: 'profit_percentage',
    label: 'PROFIT %',
    headerStyle: 'min-w-125px',
    isSorted: false,
  },
  {
    key: 'orders',
    label: 'ORDERS',
    headerStyle: 'min-w-125px',
    isSorted: false,
  },
  {
    key: 'revenue',
    label: 'REVENUE',
    headerStyle: 'min-w-125px',
    style: 'justify-content-end',
    isSorted: false,
  },
]